import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Currency formatters
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatCurrencyDetailed = (value) => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatCompact = (value) => {
  if (!value && value !== 0) return 'N/A';
  if (value >= 1000000) {
    return '$' + (value / 1000000).toFixed(1) + 'M';
  }
  if (value >= 1000) {
    return '$' + (value / 1000).toFixed(0) + 'K';
  }
  return formatCurrency(value);
};

// Pure SVG Scatter Plot for PDF Report (survives DOM cloning for print)
const ReportScatterChart = ({ scatterData, formatCurrencyShort }) => {
  if (!scatterData || !scatterData.peer_companies || scatterData.peer_companies.length === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
        No peer data available
      </div>
    );
  }

  const peers = (scatterData.peer_companies || []).map(p => ({
    x: p.participants_with_balance || 0,
    y: p.cost_per_participant || 0,
    name: p.name
  }));

  const target = scatterData.target_company ? {
    x: scatterData.target_company.participants_with_balance || 0,
    y: scatterData.target_company.cost_per_participant || 0,
    name: scatterData.target_company.name
  } : null;

  const allPoints = target ? [...peers, target] : peers;
  const xValues = allPoints.map(p => p.x);
  const yValues = allPoints.map(p => p.y);

  const xMin = Math.max(0, Math.min(...xValues) * 0.85);
  const xMax = Math.max(...xValues) * 1.15;
  const yMin = Math.max(0, Math.min(...yValues) * 0.85);
  const yMax = Math.max(...yValues) * 1.15;

  // SVG dimensions
  const width = 700;
  const height = 250;
  const margin = { top: 15, right: 20, bottom: 45, left: 65 };
  const plotW = width - margin.left - margin.right;
  const plotH = height - margin.top - margin.bottom;

  const scaleX = (v) => margin.left + ((v - xMin) / (xMax - xMin || 1)) * plotW;
  const scaleY = (v) => margin.top + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH;

  // Grid lines
  const yTicks = 5;
  const xTicks = 5;
  const yStep = (yMax - yMin) / yTicks;
  const xStep = (xMax - xMin) / xTicks;

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
        {/* Grid lines */}
        {Array.from({ length: yTicks + 1 }, (_, i) => {
          const val = yMin + i * yStep;
          const y = scaleY(val);
          return (
            <g key={`yg-${i}`}>
              <line x1={margin.left} y1={y} x2={width - margin.right} y2={y} stroke="#E5E5E5" strokeWidth="0.5" />
              <text x={margin.left - 8} y={y + 3} textAnchor="end" fontSize="8" fill="#737373">
                {formatCurrencyShort(Math.round(val))}
              </text>
            </g>
          );
        })}
        {Array.from({ length: xTicks + 1 }, (_, i) => {
          const val = xMin + i * xStep;
          const x = scaleX(val);
          return (
            <g key={`xg-${i}`}>
              <line x1={x} y1={margin.top} x2={x} y2={height - margin.bottom} stroke="#E5E5E5" strokeWidth="0.5" />
              <text x={x} y={height - margin.bottom + 14} textAnchor="middle" fontSize="8" fill="#737373">
                {Math.round(val)}
              </text>
            </g>
          );
        })}

        {/* Axis labels */}
        <text x={width / 2} y={height - 5} textAnchor="middle" fontSize="9" fill="#737373" fontWeight="600">
          Participants with Balance
        </text>
        <text x={14} y={height / 2} textAnchor="middle" fontSize="9" fill="#737373" fontWeight="600"
          transform={`rotate(-90, 14, ${height / 2})`}>
          Cost per Participant
        </text>

        {/* Peer dots */}
        {peers.map((p, i) => (
          <circle key={`peer-${i}`} cx={scaleX(p.x)} cy={scaleY(p.y)} r={4}
            fill="rgba(107, 114, 128, 0.6)" stroke="rgba(107, 114, 128, 0.8)" strokeWidth="1" />
        ))}

        {/* Target dot */}
        {target && (
          <circle cx={scaleX(target.x)} cy={scaleY(target.y)} r={8}
            fill="rgba(220, 38, 38, 0.9)" stroke="#B91C1C" strokeWidth="3" />
        )}

        {/* Legend */}
        <circle cx={width / 2 - 80} cy={height - margin.bottom + 32} r={4} fill="rgba(107, 114, 128, 0.6)" stroke="rgba(107, 114, 128, 0.8)" strokeWidth="1" />
        <text x={width / 2 - 72} y={height - margin.bottom + 35} fontSize="9" fill="#374151" fontWeight="600">Peer Plans</text>
        <circle cx={width / 2 + 20} cy={height - margin.bottom + 32} r={6} fill="rgba(220, 38, 38, 0.9)" stroke="#B91C1C" strokeWidth="2" />
        <text x={width / 2 + 30} y={height - margin.bottom + 35} fontSize="9" fill="#374151" fontWeight="600">Your Plan</text>
      </svg>
    </div>
  );
};

const PlanAnalysisReport = ({ 
  planData, 
  comparisonData, 
  historyData,
  rawHistoryData, 
  scatterData, 
  scenarioData,
  contributionCostData,
  lookbackData 
}) => {
  if (!planData || !comparisonData) {
    return null;
  }

  // DEBUG: Log data availability for troubleshooting
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    console.log('📊 PlanAnalysisReport Data Check:', {
      planData: !!planData,
      comparisonData: Array.isArray(comparisonData) ? comparisonData.length : 'not array',
      historyData: Array.isArray(historyData) ? historyData.length : 'not array',
      scatterData: !!scatterData,
      scatterPeerCompanies: scatterData?.peer_companies?.length || 0,
      scenarioData: !!scenarioData,
      scenarios: scenarioData?.scenarios?.length || 0,
      rawHistoryData: Array.isArray(rawHistoryData) ? rawHistoryData.length : 'not array',
      contributionCostData: Array.isArray(contributionCostData) ? contributionCostData.length : 'not array',
      lookbackData: !!lookbackData
    });
  }

  // Use contact_name from backend (already calculated with proper COALESCE logic)
  const sponsorSignedName = planData.contact_name || planData.sponsor_name;

  // Calculate key metrics (same as PlanAnalysisScroll)
  const latestComparison = comparisonData[0] || {};  // First item is most recent
  const costPerParticipant = latestComparison.company_cost_per_participant || 0;
  const peerAverage = latestComparison.peer_avg_cost_per_participant || 0;
  const excessCost = costPerParticipant - peerAverage;
  const costRatio = peerAverage > 0 ? costPerParticipant / peerAverage : 0;
  
  const isLowerCost = excessCost < 0;
  const absCostDifference = Math.abs(excessCost);
  const costMultiplier = isLowerCost ? (peerAverage / costPerParticipant) : costRatio;

  // Peer comparison stats - use scatterData if available for accuracy
  const totalPeers = scatterData?.total_peers || latestComparison.peer_plan_count || 0;
  const plansPayingMore = scatterData ? Math.round(totalPeers * 0.05) : (latestComparison.plans_paying_more || 0);
  const plansPayingLess = totalPeers - plansPayingMore;

  // Historical loss calculation
  let historicalLoss = 0;
  let historicalPerParticipant = 0;
  
  if (lookbackData && lookbackData.lookback_scenarios && lookbackData.lookback_scenarios.length > 0) {
    const maxLookback = Math.max(...lookbackData.lookback_scenarios.map(s => s.lookback_years));
    const maxLookbackScenarios = lookbackData.lookback_scenarios.filter(s => s.lookback_years === maxLookback);
    const bestScenarioType = lookbackData.best_scenario_type;
    const bestScenario = maxLookbackScenarios.find(s => s.scenario_type === bestScenarioType) || maxLookbackScenarios[0];
    
    if (bestScenario) {
      historicalLoss = bestScenario.total_economic_benefit || 0;
      historicalPerParticipant = bestScenario.benefit_per_participant || 0;
    }
  }
  
  if (historicalLoss === 0) {
    comparisonData.forEach(year => {
      if (year.company_cost_per_participant && year.peer_avg_cost_per_participant && year.company_participants) {
        const yearlyExcess = (year.company_cost_per_participant - year.peer_avg_cost_per_participant) * year.company_participants;
        historicalLoss += yearlyExcess;
      }
    });
    historicalPerParticipant = historicalLoss / (planData.participants || 1);
  }

  // Calculate the earliest year for historical analysis
  // Use the LATER of: plan effective date OR first filing year in our database
  let historicalStartYear = 2009; // Default fallback
  
  try {
    // Get plan effective date year
    let planEffYear = null;
    if (planData?.plan_eff_date) {
      const effDate = new Date(planData.plan_eff_date);
      if (!isNaN(effDate.getTime())) {
        planEffYear = effDate.getFullYear();
      }
    }
    
    // Get first filing year from comparison data (comparisonData is sorted newest first, so last item is oldest)
    let firstFilingYear = null;
    if (comparisonData && comparisonData.length > 0) {
      // Find minimum year from all comparison data
      const years = comparisonData
        .map(item => item.plan_year)
        .filter(year => year != null);
      
      if (years.length > 0) {
        firstFilingYear = Math.min(...years);
      }
    }
    
    // Use the LATER (more recent) of the two dates - this is the actual start of our historical data
    if (planEffYear && firstFilingYear) {
      historicalStartYear = Math.max(planEffYear, firstFilingYear);
    } else if (firstFilingYear) {
      historicalStartYear = firstFilingYear;
    } else if (planEffYear) {
      historicalStartYear = planEffYear;
    }
  } catch (error) {
    console.error('Error calculating historical start year:', error);
  }

  // Future impact calculation
  const providerScenarios25yr = scenarioData?.scenarios?.filter(s => 
    s.projection_years === 25 && 
    s.scenario_type?.startsWith('PROVIDER_')
  ) || [];
  
  const best25YearData = providerScenarios25yr.reduce((best, current) => {
    const bestCost = best?.avg_alt_annual_fee || Infinity;
    const currentCost = current?.avg_alt_annual_fee || Infinity;
    return currentCost < bestCost ? current : best;
  }, null);

  const futureExcessFees = best25YearData?.projected_total_savings || 0;
  const futurePerParticipant = best25YearData?.projected_savings_per_participant || 0;

  // Scenario configuration
  const getScenarioConfig = (scenarioType) => {
    const configs = {
      'PROVIDER_FLAT_FEE_PRTCP_ONLY': { name: 'Flat Fee Per Participant', description: 'Flat fee per participant only', color: '#10b981', priority: 1 },
      'PROVIDER_FLAT_FEE_P+AUA': { name: 'Flat Fee + AUA', description: 'Flat fee per participant + asset-based', color: '#0E8376', priority: 2 },
      'PROVIDER_FLAT_FEE_ADMIN+P': { name: 'Flat Fee Admin + Participant', description: 'Base admin fee + per participant', color: '#33BFAF', priority: 3 },
      'PROVIDER_BENCHMARK': { name: 'Benchmark Provider', description: 'Generic low-cost option', color: '#12A493', priority: 4 },
      'PROVIDER_MID_STD': { name: 'Industry Standard', description: 'Mid-market pricing', color: '#7C5CDB', priority: 5 },
      'PROVIDER_HIGH_TOUCH': { name: 'High Touch Service', description: 'Premium service model', color: '#f59e0b', priority: 6 },
      'PROVIDER_HIGH_ASSET_FEES75BPS': { name: 'High Asset Fees', description: 'Asset-based pricing', color: '#ef4444', priority: 7 }
    };
    return configs[scenarioType] || { name: scenarioType, description: '', color: '#6b7280', priority: 99 };
  };

  return (
    <div className="pdf-report" style={{ 
      position: 'absolute',
      left: '-9999px',
      top: '0',
      width: '8.5in',
      background: 'white'
    }}>
      <style>{`
        @media print {
          .pdf-report {
            display: block !important;
          }
          .report-page {
            page-break-after: always;
            page-break-inside: avoid;
            width: 8.5in;
            height: 11in;
            padding: 0.75in;
            box-sizing: border-box;
            background: white;
            position: relative;
          }
          .report-page:last-child {
            page-break-after: auto;
          }
          .no-break {
            page-break-inside: avoid;
          }
          body {
            margin: 0;
            padding: 0;
          }
          /* Force charts to render properly in print */
          .recharts-responsive-container {
            position: relative !important;
          }
          .recharts-wrapper {
            position: relative !important;
          }
          /* Force backgrounds/gradients to print */
          .print-bg {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }

        .pdf-report {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          color: #111827;
          line-height: 1.5;
        }

        .report-page {
          position: relative;
        }

        .page-header {
          border-bottom: 2px solid #1e3a8a;
          padding-bottom: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .page-footer {
          position: absolute;
          bottom: 0.5in;
          left: 0.75in;
          right: 0.75in;
          border-top: 1px solid #e5e7eb;
          padding-top: 0.5rem;
          font-size: 0.75rem;
          color: #6b7280;
          text-align: center;
        }
      `}</style>

      {/* PAGE 1: COVER PAGE */}
      <div className="report-page">
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center', textAlign: 'center' }}>
          <div style={{ marginBottom: '3rem' }}>
            <div style={{ fontSize: '3rem', fontWeight: 900, color: '#1e3a8a', marginBottom: '1rem', letterSpacing: '-0.02em' }}>
              401(k) Plan Analysis Report
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#374151', marginBottom: '2rem' }}>
              {planData.sponsor_name}
            </div>
            <div style={{ fontSize: '1rem', color: '#6b7280' }}>
              Plan #{planData.plan_number} | {planData.plan_year}
            </div>
          </div>

          <div style={{ 
            background: isLowerCost ? '#f0fdf4' : '#fef2f2', 
            border: `3px solid ${isLowerCost ? '#86efac' : '#fca5a5'}`,
            borderRadius: '12px',
            padding: '2rem',
            margin: '0 auto',
            maxWidth: '500px'
          }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: isLowerCost ? '#166534' : '#991b1b', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              KEY FINDING
            </div>
            <div style={{ fontSize: '2.5rem', fontWeight: 900, color: isLowerCost ? '#16a34a' : '#dc2626', marginBottom: '0.5rem', letterSpacing: '-0.01em' }}>
              {costMultiplier.toFixed(1)}× {isLowerCost ? 'Lower' : 'Higher'}
            </div>
            <div style={{ fontSize: '1rem', color: '#374151' }}>
              than industry average
            </div>
          </div>

          <div style={{ marginTop: '3rem', padding: '2rem', background: '#f9fafb', borderRadius: '8px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2rem', textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: 600 }}>YOUR COST</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>{formatCurrency(costPerParticipant)}</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>per participant</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: 600 }}>PEER AVERAGE</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e3a8a' }}>{formatCurrency(peerAverage)}</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>per participant</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: 600 }}>
                  {isLowerCost ? 'SAVINGS' : 'EXCESS FEES'}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: isLowerCost ? '#16a34a' : '#dc2626' }}>
                  {formatCurrency(absCostDifference)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>per participant</div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="page-footer">
          Generated by Love Your 401k | {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* PAGE 2: EXECUTIVE SUMMARY */}
      <div className="report-page">
        <div className="page-header">
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e3a8a', margin: 0 }}>Executive Summary</h1>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>Plan Overview</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Sponsor</div>
              <div style={{ fontWeight: 600 }}>{planData.sponsor_name}</div>
            </div>
            <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>EIN</div>
              <div style={{ fontWeight: 600 }}>{planData.ein}</div>
            </div>
            <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Participants</div>
              <div style={{ fontWeight: 600 }}>{planData.participants?.toLocaleString()}</div>
            </div>
            <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px' }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Total Assets</div>
              <div style={{ fontWeight: 600 }}>{formatCurrency(planData.assets || planData.total_assets)}</div>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>Cost Analysis Summary</h2>
          <div style={{ background: isLowerCost ? '#f0fdf4' : '#fef2f2', padding: '1.5rem', borderRadius: '8px', border: `2px solid ${isLowerCost ? '#86efac' : '#fca5a5'}` }}>
            <p style={{ fontSize: '1rem', lineHeight: 1.7, margin: 0, marginBottom: '1rem' }}>
              {isLowerCost ? (
                <>Your plan is achieving better value compared to similar companies in your industry. Out of {totalPeers.toLocaleString()} comparable plans, only {plansPayingLess.toLocaleString()} plans have lower costs than yours.</>
              ) : (
                <>Your plan costs are significantly higher than industry averages. Out of {totalPeers.toLocaleString()} comparable plans analyzed, only {plansPayingMore.toLocaleString()} plans pay more than you do. This means {plansPayingLess.toLocaleString()} companies are paying less for their 401(k) administration.</>
              )}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Per Participant Cost</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827' }}>{formatCurrency(costPerParticipant)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  {isLowerCost ? 'Annual Savings' : 'Annual Excess Fees'}
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: isLowerCost ? '#16a34a' : '#dc2626' }}>
                  {formatCurrency(absCostDifference * (planData.participants || 0))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {!isLowerCost && (
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>Historical Impact</h2>
            <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Total Excess Fees Since {historicalStartYear}</div>
                  <div style={{ fontSize: '2rem', fontWeight: 900, color: '#dc2626', letterSpacing: '-0.01em' }}>
                    {formatCurrency(Math.abs(historicalLoss))}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Per Participant</div>
                  <div style={{ fontSize: '2rem', fontWeight: 900, color: '#111827', letterSpacing: '-0.01em' }}>
                    {formatCurrency(Math.abs(historicalPerParticipant))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isLowerCost && (
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>Historical Savings</h2>
            <div style={{ background: '#f0fdf4', padding: '1.5rem', borderRadius: '8px', border: '1px solid #86efac' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#166534', marginBottom: '0.5rem' }}>Total Savings Since {historicalStartYear}</div>
                  <div style={{ fontSize: '2rem', fontWeight: 900, color: '#16a34a', letterSpacing: '-0.01em' }}>
                    {formatCurrency(Math.abs(historicalLoss))}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#166534', marginBottom: '0.5rem' }}>Per Participant</div>
                  <div style={{ fontSize: '2rem', fontWeight: 900, color: '#15803d', letterSpacing: '-0.01em' }}>
                    {formatCurrency(Math.abs(historicalPerParticipant))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="page-footer">
          {planData.sponsor_name} | 401(k) Plan Analysis | Page 2
        </div>
      </div>

      {/* PAGE 2B: SCATTER CHART (own page to avoid overflow clipping) */}
      {(() => {
        try {
          if (!scatterData || !scatterData.peer_companies || !Array.isArray(scatterData.peer_companies) || scatterData.peer_companies.length === 0) {
            return null;
          }
          return (
            <div className="report-page">
              <div className="page-header">
                <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e3a8a', margin: 0 }}>How Your Plan Compares</h1>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                This chart shows your plan's cost per participant compared to {scatterData.total_peers?.toLocaleString() || '0'} similar plans in your industry and size category ({scatterData.size_range}).
              </div>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.75rem', textAlign: 'center' }}>
                Cost Distribution vs Peer Group ({scatterData.target_year || 'Latest'})
              </div>
              <div style={{ background: '#fafbfc', padding: '1.5rem', borderRadius: '8px', height: '380px', position: 'relative' }}>
                <ReportScatterChart scatterData={scatterData} formatCurrencyShort={formatCurrency} />
              </div>
              <div className="page-footer">
                {planData.sponsor_name} | 401(k) Plan Analysis | Page 3
              </div>
            </div>
          );
        } catch (chartError) {
          console.error('Scatter chart error:', chartError);
          return null;
        }
      })()}

      {/* PAGE 3: HISTORICAL IMPACT WITH LINE CHART */}
      {(() => {
        try {
          return (
            <div className="report-page">
              <div className="page-header">
                <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e3a8a', margin: 0 }}>Historical Impact Analysis</h1>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                      {isLowerCost ? `Total Savings Achieved Since ${historicalStartYear}` : 'Total Opportunity Cost Of High Fees'}
                    </div>
                    <div style={{ fontSize: '2.5rem', fontWeight: 900, color: isLowerCost ? '#16a34a' : '#dc2626', letterSpacing: '-0.01em' }}>
                      {formatCurrency(Math.abs(historicalLoss))}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>since {historicalStartYear}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Per Participant</div>
                    <div style={{ fontSize: '2.5rem', fontWeight: 900, color: '#111827', letterSpacing: '-0.01em' }}>
                      {formatCurrency(Math.abs(historicalPerParticipant))}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      {isLowerCost ? 'savings achieved' : 'missed opportunity'}
                    </div>
                  </div>
                </div>

                {/* HISTORICAL LINE CHART - WITH ERROR HANDLING */}
                {(() => {
                  try {
                    if (!historyData || !Array.isArray(historyData) || historyData.length === 0) {
                      return (
                        <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '8px', textAlign: 'center', color: '#6b7280' }}>
                          Historical cost trend data not available
                        </div>
                      );
                    }

                    return (
                      <div style={{ background: '#fafbfc', padding: '1.5rem', borderRadius: '8px' }}>
                        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
                          Cost Per Participant Over Time
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={historyData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                            <XAxis 
                              dataKey="plan_year" 
                              tick={{ fill: '#737373', fontSize: 10 }}
                            />
                            <YAxis 
                              tick={{ fill: '#737373', fontSize: 10 }}
                              tickFormatter={(value) => {
                                try {
                                  return formatCurrency(value);
                                } catch (e) {
                                  return '$0';
                                }
                              }}
                            />
                            <Tooltip 
                              formatter={(value) => {
                                try {
                                  return formatCurrency(value);
                                } catch (e) {
                                  return '$0';
                                }
                              }}
                              contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.75rem' }}
                            />
                            <Legend 
                              wrapperStyle={{ fontSize: '11px' }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="peer_avg_cost_per_participant" 
                              stroke="#9ca3af" 
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              name="Peer Average"
                              dot={false}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="company_cost_per_participant" 
                              stroke="#14b8a6" 
                              strokeWidth={3}
                              name="Your Plan"
                              dot={{ r: 3, fill: '#14b8a6' }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    );
                  } catch (chartError) {
                    console.error('Historical chart error:', chartError);
                    return (
                      <div style={{ background: '#fef2f2', padding: '1.5rem', borderRadius: '8px', border: '1px solid #fca5a5' }}>
                        <div style={{ fontSize: '0.875rem', color: '#991b1b', textAlign: 'center' }}>
                          Chart rendering unavailable - see year-by-year data on next page
                        </div>
                      </div>
                    );
                  }
                })()}
              </div>

              <div className="page-footer">
                {planData.sponsor_name} | 401(k) Plan Analysis | Page 4
              </div>
            </div>
          );
        } catch (pageError) {
          console.error('PAGE 3 ERROR:', pageError);
          // Return empty div so rendering continues
          return <div style={{ display: 'none' }}></div>;
        }
      })()}

      {/* PAGE 4: FUTURE IMPACT ANALYSIS - Only show if plan is higher cost than peers */}
      {!isLowerCost && futureExcessFees > 0 && (
        <div className="report-page">
          <div className="page-header">
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e3a8a', margin: 0 }}>Future Impact Analysis</h1>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <p style={{ fontSize: '1rem', color: '#6b7280', lineHeight: 1.7, marginBottom: '2rem' }}>
              Over the next 25 years, your plan fees will further erode your savings
            </p>

            <div style={{ marginBottom: '2rem' }}>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.75rem', fontWeight: 500 }}>
                Future Excess Fees
              </div>
              <div style={{ fontSize: '3rem', fontWeight: 900, color: '#1e3a8a', marginBottom: '0.5rem', letterSpacing: '-0.02em', lineHeight: 1 }}>
                {formatCurrency(futureExcessFees)}
              </div>
              <div style={{ fontSize: '1.125rem', color: '#111827', fontWeight: 600 }}>
                {formatCurrency(futurePerParticipant)} Per Participant
              </div>
            </div>

            <div style={{ background: '#f3f4f6', padding: '1.25rem', borderRadius: '8px', marginBottom: '2rem' }}>
              <p style={{ fontSize: '0.9375rem', color: '#374151', lineHeight: 1.8, margin: 0 }}>
                Every year you delay switching to a lower-cost provider, your participants lose not just that year's fee savings, but also all the compound growth those savings would have generated over the remaining years until retirement.
              </p>
            </div>

            {/* FUTURE PROJECTION CHART */}
            {(() => {
              const scenarios = scenarioData?.scenarios || (Array.isArray(scenarioData) ? scenarioData : null);
              
              if (scenarios && scenarios.length > 0) {
                // Prepare chart data
                const timeframes = [5, 10, 15, 20, 25];
                const chartData = timeframes.map(years => {
                  const dataPoint = { years };
                  
                  // Find current plan data
                  const currentScenario = scenarios.find(s => s.projection_years === years);
                  if (currentScenario) {
                    dataPoint['Current Plan'] = currentScenario.projected_balance_current_costs;
                  }
                  
                  // Find best provider scenario (flat fee)
                  const flatFeeScenario = scenarios.find(
                    s => s.projection_years === years && 
                    (s.scenario_type === 'PROVIDER_FLAT_FEE_PRTCP_ONLY' || 
                     s.scenario_type?.includes('FLAT_FEE'))
                  );
                  if (flatFeeScenario) {
                    dataPoint['Flat Fee Per Participant'] = flatFeeScenario.projected_balance_alt_costs;
                  }
                  
                  return dataPoint;
                });

                const assumedReturn = scenarios[0]?.assumed_return_rate ? (scenarios[0].assumed_return_rate * 100).toFixed(1) : '7.0';

                return (
                  <div style={{ background: '#fafbfc', padding: '1.5rem', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                        Projected Balance Growth Comparison
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', background: '#f3f4f6', padding: '0.375rem 0.75rem', borderRadius: '6px' }}>
                        Assumed return rate: {assumedReturn}%
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                        <XAxis 
                          dataKey="years" 
                          label={{ value: 'Years from Now', position: 'insideBottom', offset: -5, fill: '#737373', fontSize: 10 }}
                          tick={{ fill: '#737373', fontSize: 10 }}
                        />
                        <YAxis 
                          label={{ value: 'Projected Balance ($)', angle: -90, position: 'insideLeft', fill: '#737373', fontSize: 10 }}
                          tickFormatter={(value) => {
                            try {
                              return formatCompact(value);
                            } catch (e) {
                              return '$0';
                            }
                          }}
                          tick={{ fill: '#737373', fontSize: 10 }}
                        />
                        <Tooltip 
                          formatter={(value) => {
                            try {
                              return formatCurrency(value);
                            } catch (e) {
                              return '$0';
                            }
                          }}
                          labelFormatter={(label) => `${label} Years`}
                          contentStyle={{ 
                            background: 'white', 
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '0.75rem'
                          }}
                        />
                        <Legend 
                          wrapperStyle={{ fontSize: '11px' }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="Current Plan" 
                          stroke="#f87171" 
                          strokeWidth={3}
                          strokeDasharray="5 5"
                          dot={{ r: 3, fill: '#f87171' }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="Flat Fee Per Participant" 
                          stroke="#10b981" 
                          strokeWidth={3}
                          dot={{ r: 3, fill: '#10b981' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          <div className="page-footer">
            {planData.sponsor_name} | 401(k) Plan Analysis | Page 5
          </div>
        </div>
      )}

      {/* PAGE 5 (or 4 if lower cost): YEAR-BY-YEAR COST BREAKDOWN */}
      <div className="report-page">
        <div className="page-header">
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e3a8a', margin: 0 }}>Cost Per Participant - Year-by-Year Cost</h1>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{ fontSize: '0.9375rem', color: '#6b7280', lineHeight: 1.6 }}>
            This table shows the annual comparison between your plan's costs and the average costs of comparable plans in your industry.
          </p>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Year</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>Your Cost</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>Peer Average</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>Difference</th>
              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>Similar Plans</th>
            </tr>
          </thead>
          <tbody>
            {comparisonData.map((year, idx) => {
              const difference = year.company_cost_per_participant - year.peer_avg_cost_per_participant;
              const isDiffNegative = difference < 0;
              return (
                <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '0.75rem' }}>{year.plan_year}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>
                    {formatCurrencyDetailed(year.company_cost_per_participant)}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right', color: '#1e3a8a' }}>
                    {formatCurrencyDetailed(year.peer_avg_cost_per_participant)}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, color: isDiffNegative ? '#16a34a' : '#dc2626' }}>
                    {isDiffNegative ? '' : '+'}{formatCurrencyDetailed(difference)}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                    {year.peer_plan_count?.toLocaleString() || 'N/A'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ marginTop: '2rem', background: '#fffbeb', padding: '1rem', borderRadius: '8px', border: '1px solid #fcd34d' }}>
          <div style={{ fontSize: '0.875rem', color: '#92400e', lineHeight: 1.6 }}>
            <strong>Note:</strong> Peer averages are calculated from plans with similar participant counts in the same industry (NAICS code).
          </div>
        </div>

        <div className="page-footer">
          {planData.sponsor_name} | 401(k) Plan Analysis | Page {!isLowerCost && futureExcessFees > 0 ? '6' : '5'}
        </div>
      </div>


      {/* PAGE 6 (or 5): COMPLETE HISTORICAL DATA */}
      {rawHistoryData && rawHistoryData.length > 0 && (
        <div className="report-page">
          <div className="page-header">
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e3a8a', margin: 0 }}>Complete Historical Data</h1>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontSize: '0.9375rem', color: '#6b7280', lineHeight: 1.6 }}>
              Historical plan metrics including participants, assets, contributions, and administrative costs.
            </p>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600 }}>Year</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>Participants</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>Assets</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>Contributions</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>Admin Cost</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>Cost/Participant</th>
              </tr>
            </thead>
            <tbody>
              {rawHistoryData.map((year, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '0.5rem' }}>{year.plan_year || year.year}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                    {year.participants !== null && year.participants !== undefined ? year.participants.toLocaleString() : 'N/A'}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                    {formatCurrency(year.assets || year.total_assets)}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                    {formatCurrency(year.contributions || year.total_contributions)}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                    {formatCurrency(year.admin_expenses || year.total_admin_expenses)}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>
                    {formatCurrency(year.cost_per_participant)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="page-footer">
            {planData.sponsor_name} | 401(k) Plan Analysis | Page {!isLowerCost && futureExcessFees > 0 ? '8' : '7'}
          </div>
        </div>
      )}

      {/* PAGE 6: CONTRIBUTIONS ANALYSIS */}
      {contributionCostData && contributionCostData.length > 0 && (
        <div className="report-page">
          <div className="page-header">
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e3a8a', margin: 0 }}>Annual Contributions Analysis</h1>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontSize: '0.9375rem', color: '#6b7280', lineHeight: 1.6 }}>
              {isLowerCost ? (
                <>Your plan is efficiently managing costs, allowing more of your contributions to grow over time.</>
              ) : (
                <>Analysis of how administrative costs impact participant and employer contributions.</>
              )}
            </p>
          </div>

          <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[...contributionCostData].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="plan_year" 
                    tick={{ fontSize: '0.75rem', fill: '#6b7280' }}
                    stroke="#9ca3af"
                  />
                  <YAxis 
                    tickFormatter={(value) => `${value.toFixed(2)}%`}
                    tick={{ fontSize: '0.75rem', fill: '#6b7280' }}
                    stroke="#9ca3af"
                    width={65}
                    label={{ value: '% of Contributions', angle: -90, position: 'insideLeft', dx: -10, style: { fontSize: '0.7rem', fill: '#6b7280' } }}
                  />
                  <Tooltip formatter={(value) => `${value?.toFixed(2)}%`} />
                  <Bar dataKey="admin_cost_as_pct_of_total_contrib" fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '8px' }}>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={[...contributionCostData].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="plan_year" 
                    tick={{ fontSize: '0.75rem', fill: '#6b7280' }}
                    stroke="#9ca3af"
                  />
                  <YAxis 
                    tickFormatter={(value) => `${value.toFixed(2)}%`}
                    tick={{ fontSize: '0.75rem', fill: '#6b7280' }}
                    stroke="#9ca3af"
                    width={65}
                    label={{ value: '% of Plan Assets', angle: -90, position: 'insideLeft', dx: -10, style: { fontSize: '0.7rem', fill: '#6b7280' } }}
                  />
                  <Tooltip formatter={(value) => `${value?.toFixed(2)}%`} />
                  <Line type="monotone" dataKey="admin_cost_as_pct_of_assets" stroke="#6366f1" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="page-footer">
            {planData.sponsor_name} | 401(k) Plan Analysis | Page {!isLowerCost && futureExcessFees > 0 ? '8' : '7'}
          </div>
        </div>
      )}

      {/* PAGE 7: ALTERNATIVE FEE SCENARIOS */}
      {scenarioData && scenarioData.scenarios && (() => {
        const scenariosByType = {};
        scenarioData.scenarios.forEach(scenario => {
          if (scenario.scenario_type?.startsWith('PROVIDER_')) {
            if (!scenariosByType[scenario.scenario_type]) {
              scenariosByType[scenario.scenario_type] = [];
            }
            scenariosByType[scenario.scenario_type].push(scenario);
          }
        });

        const sortedScenarioTypes = Object.keys(scenariosByType).sort((a, b) => {
          return (getScenarioConfig(a)?.priority || 99) - (getScenarioConfig(b)?.priority || 99);
        });

        const displayedScenarios = sortedScenarioTypes.slice(0, 6);
        const bestScenario = sortedScenarioTypes[0];

        return (
          <div className="report-page">
            <div className="page-header">
              <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e3a8a', margin: 0 }}>Alternative Fee Scenarios</h1>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '0.9375rem', color: '#6b7280', lineHeight: 1.6 }}>
                Projected plan balances under different fee structures over 5, 10, 15, 20, and 25 years.
              </p>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Scenario</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>Annual Cost</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>5 Years</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>10 Years</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>15 Years</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>20 Years</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>25 Years</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>Total Savings</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Current Plan Row */}
                  <tr style={{ background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: 600 }}>Current Plan</div>
                      <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>Your existing costs</div>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600, color: '#ef4444' }}>
                      {scenarioData?.current_costs?.current_annual_admin_cost !== undefined 
                        ? formatCurrency(scenarioData.current_costs.current_annual_admin_cost)
                        : 'N/A'}
                    </td>
                    {[5, 10, 15, 20, 25].map(years => {
                      const data = scenariosByType[bestScenario]?.find(s => s.projection_years === years);
                      return (
                        <td key={years} style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 500 }}>
                          {data && formatCompact(data.projected_balance_current_costs)}
                        </td>
                      );
                    })}
                    <td style={{ padding: '0.75rem', textAlign: 'right', color: '#9ca3af' }}>—</td>
                  </tr>

                  {/* Alternative Scenarios */}
                  {displayedScenarios.map(scenarioType => {
                    const config = getScenarioConfig(scenarioType);
                    const scenarios = scenariosByType[scenarioType] || [];
                    const data25yr = scenarios.find(s => s.projection_years === 25);
                    
                    return (
                      <tr key={scenarioType} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem' }}>
                          <div style={{ fontWeight: 600, color: config.color }}>{config.name}</div>
                          <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>{config.description}</div>
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>
                          {data25yr && formatCurrency(data25yr.avg_alt_annual_fee)}
                        </td>
                        {[5, 10, 15, 20, 25].map(years => {
                          const data = scenarios.find(s => s.projection_years === years);
                          return (
                            <td key={years} style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 500 }}>
                              {data && formatCompact(data.projected_balance_alt_costs)}
                            </td>
                          );
                        })}
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                          {data25yr && (
                            <div>
                              <div style={{ fontWeight: 700, color: data25yr.projected_total_savings >= 0 ? '#10b981' : '#ef4444' }}>
                                {data25yr.projected_total_savings >= 0 ? '' : '-'}{formatCompact(Math.abs(data25yr.projected_total_savings))}
                              </div>
                              <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>
                                {data25yr.projected_savings_per_participant >= 0 ? '' : '-'}{formatCurrency(Math.abs(data25yr.projected_savings_per_participant))}/person
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '1.5rem', background: '#fffbeb', padding: '1rem', borderRadius: '8px', border: '1px solid #fcd34d' }}>
              <div style={{ fontSize: '0.875rem', color: '#92400e', lineHeight: 1.6 }}>
                <strong>Note:</strong> Projections assume a 7% annual return and current contribution levels. Actual results may vary.
              </div>
            </div>

            <div className="page-footer">
              {planData.sponsor_name} | 401(k) Plan Analysis | Page {(() => {
                let page = 7;
                if (!isLowerCost && futureExcessFees > 0) page++;
                if (rawHistoryData && rawHistoryData.length > 0) page++;
                if (contributionCostData && contributionCostData.length > 0) page++;
                return page;
              })()}
            </div>
          </div>
        );
      })()}

      {/* PAGE 8: PLAN CHARACTERISTICS */}
      {planData.plan_characteristics && (
        <div className="report-page">
          <div className="page-header">
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e3a8a', margin: 0 }}>Plan Characteristics</h1>
          </div>

          {planData.business_description && (
            <div style={{ background: '#f0fdf4', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #86efac' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#166534', marginBottom: '0.25rem' }}>INDUSTRY</div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: '#15803d' }}>{planData.business_description}</div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            {planData.plan_characteristics.plan_category && (
              <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Plan Category</div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1e3a8a' }}>{planData.plan_characteristics.plan_category}</div>
              </div>
            )}
            
            {planData.plan_characteristics.plan_subtype && (
              <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Plan Subtype</div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1e3a8a' }}>{planData.plan_characteristics.plan_subtype}</div>
              </div>
            )}
          </div>

          {planData.plan_characteristics.pension_code_descriptions && planData.plan_characteristics.pension_code_descriptions.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>Plan Features</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                {planData.plan_characteristics.pension_code_descriptions.map((item, idx) => (
                  <div key={idx} style={{ background: '#f9fafb', padding: '0.75rem', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>{item.code}</div>
                    <div style={{ fontSize: '0.875rem', color: '#374151' }}>{item.description}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="page-footer">
            {planData.sponsor_name} | 401(k) Plan Analysis | Page {(() => {
              let page = 8;
              if (!isLowerCost && futureExcessFees > 0) page++;
              if (rawHistoryData && rawHistoryData.length > 0) page++;
              if (contributionCostData && contributionCostData.length > 0) page++;
              return page;
            })()}
          </div>
        </div>
      )}

      {/* PAGE 9: NEXT STEPS */}
      <div className="report-page">
        <div className="page-header">
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1e3a8a', margin: 0 }}>Next Steps</h1>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>
            {isLowerCost ? 'Maintain Your Competitive Advantage' : 'Reduce Your Plan Costs'}
          </h2>
          <p style={{ fontSize: '0.9375rem', color: '#6b7280', lineHeight: 1.7 }}>
            {isLowerCost ? (
              <>Your plan is already achieving excellent value compared to industry peers. We can help you maintain this competitive advantage and ensure you continue to get the best service for your participants.</>
            ) : (
              <>Based on this analysis, there are significant opportunities to reduce your plan's administrative costs while maintaining or improving service quality. Lower fees mean more money stays invested for your participants' retirement.</>
            )}
          </p>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>Recommended Actions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* Action 1: For Plan Participants */}
            <div>
              <div style={{ fontWeight: 600, marginBottom: '0.75rem' }}>
                <span style={{ marginRight: '1rem' }}>1</span>If you are a Plan Participant:
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.7 }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  Please send this analysis to your Human Resources Department with the following message:
                </div>
                <div style={{ 
                  marginTop: '0.5rem', 
                  marginBottom: '1rem',
                  padding: '0.75rem', 
                  background: '#f9fafb', 
                  borderLeft: '3px solid #1e3a8a',
                  fontStyle: 'italic',
                  fontSize: '0.8125rem'
                }}>
                  "I've reviewed our plan fees and believe some may be higher than normal for plans our size. Could the Company consider benchmarking our plan or conducting a fee review? Lower fees would help all participants."
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  You may also wish to copy your 401(k) Plan Sponsor <strong>{sponsorSignedName}</strong> and ask:
                </div>
                <div style={{ 
                  marginTop: '0.5rem', 
                  padding: '0.75rem', 
                  background: '#f9fafb', 
                  borderLeft: '3px solid #1e3a8a',
                  fontStyle: 'italic',
                  fontSize: '0.8125rem'
                }}>
                  "Can you share the most recent fee benchmarking report for our 401(k)? If the Company hasn't prepared one in the past 2-3 years, attached is a copy of a report from LoveYour401k.com that benchmarks our fees against other 401(k) plans of comparable size - our fees look to be significantly higher."
                </div>
              </div>
            </div>
            
            {/* Action 2: For Plan Sponsors */}
            <div>
              <div style={{ fontWeight: 600, marginBottom: '0.75rem' }}>
                <span style={{ marginRight: '1rem' }}>2</span>If you are a Plan Sponsor:
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.7 }}>
                Please <a href="https://loveyour401k.com/contact" style={{ color: '#1e3a8a', textDecoration: 'underline' }}>click here to contact us</a> and we can assist you in finding a lower cost plan. By doing so, you fulfill your fiduciary duties as your 401(k) Plan Sponsor and reduce any liability by keeping your 401(k) plan with a high cost provider.
              </div>
            </div>
            
            {/* Action 3: Benchmark Regularly */}
            <div>
              <div style={{ fontWeight: 600, marginBottom: '0.75rem' }}>
                <span style={{ marginRight: '1rem' }}>3</span>Benchmark Regularly
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.7 }}>
                Benchmark your plan regularly to ensure that you are not being charged excess fees that can erode your future retirement savings. Everyone in your 401(k) plan benefits from lower fees - make sure you are not losing your money to fees that exceed comparable plans.
              </div>
            </div>
          </div>
        </div>

        <div className="page-footer">
          {planData.sponsor_name} | 401(k) Plan Analysis | Page {(() => {
            let page = 9;
            if (!isLowerCost && futureExcessFees > 0) page++;
            if (rawHistoryData && rawHistoryData.length > 0) page++;
            if (contributionCostData && contributionCostData.length > 0) page++;
            return page;
          })()}
        </div>
      </div>

      {/* FINAL PAGE: CALL TO ACTION */}
      <div className="report-page">
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <div className="print-bg" style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            padding: '3rem',
            borderRadius: '12px',
            color: 'white',
            textAlign: 'center',
            width: '100%',
            maxWidth: '500px',
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact'
          }}>
            <h3 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '1.5rem', margin: 0 }}>
              Ready to Take Action?
            </h3>
            <p style={{ fontSize: '1.125rem', marginBottom: '2rem', opacity: 0.9 }}>
              Contact us to discuss how we can help you {isLowerCost ? 'maintain your competitive advantage' : 'reduce plan costs and improve participant outcomes'}.
            </p>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>
              Love Your 401k
            </div>
            <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
              loveyour401k.com
            </div>
            <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '1rem' }}>
              Powered by Form 5500 data
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanAnalysisReport;
